import { db } from './db.js';
import { BaseEntity } from '../types.js';
import { ListParams } from './repository.js';
import { attemptSessionRenewal, clearAuthSession, getAuthToken } from './auth.js';

const getEnv = (key: string): string => {
  if (typeof process !== 'undefined' && process.env && typeof process.env[key] === 'string') {
    return process.env[key] as string;
  }
  if (typeof import.meta !== 'undefined' && (import.meta as any).env && (import.meta as any).env[key]) {
    return (import.meta as any).env[key];
  }
  return '';
};

const API_BASE = getEnv('VITE_API_URL') || '';
const buildUrl = (path: string) => `${API_BASE}${path}`;

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
const parseRetryAfterMs = (headerValue: string | null): number => {
  if (!headerValue) return 1500;
  const seconds = Number(headerValue);
  if (!Number.isNaN(seconds) && Number.isFinite(seconds)) return Math.max(250, Math.floor(seconds * 1000));
  const dateTs = Date.parse(headerValue);
  if (!Number.isNaN(dateTs)) return Math.max(250, dateTs - Date.now());
  return 1500;
};

export interface ServerRateLimitStatus {
  isBusy: boolean;
  retryingRequests: number;
  nextAllowedAt: number | null;
  domain: string | null;
}

interface DomainRateLimitState {
  nextAllowedAt: number;
}

const domainRateLimits = new Map<string, DomainRateLimitState>();
const rateLimitListeners = new Set<(status: ServerRateLimitStatus) => void>();
let activeRetryingRequests = 0;

const getDomainKey = (path: string): string => {
  try {
    const baseOrigin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
    return new URL(buildUrl(path), baseOrigin).origin;
  } catch {
    return API_BASE || 'local-api';
  }
};

const getLatestRateLimit = () => {
  const now = Date.now();
  let latestDomain: string | null = null;
  let latestAllowedAt: number | null = null;

  domainRateLimits.forEach((state, domain) => {
    if (state.nextAllowedAt <= now) return;
    if (latestAllowedAt === null || state.nextAllowedAt > latestAllowedAt) {
      latestAllowedAt = state.nextAllowedAt;
      latestDomain = domain;
    }
  });

  return { latestAllowedAt, latestDomain };
};

const notifyRateLimitListeners = () => {
  const { latestAllowedAt, latestDomain } = getLatestRateLimit();
  const status: ServerRateLimitStatus = {
    isBusy: activeRetryingRequests > 0 || latestAllowedAt !== null,
    retryingRequests: activeRetryingRequests,
    nextAllowedAt: latestAllowedAt,
    domain: latestDomain,
  };
  rateLimitListeners.forEach((listener) => listener(status));
};

const setRateLimitCooldown = (domain: string, waitMs: number) => {
  const nextAllowedAt = Date.now() + waitMs;
  const existing = domainRateLimits.get(domain);
  if (!existing || nextAllowedAt > existing.nextAllowedAt) {
    domainRateLimits.set(domain, { nextAllowedAt });
    notifyRateLimitListeners();
  }
};

const waitForDomainAvailability = async (domain: string) => {
  while (true) {
    const remaining = (domainRateLimits.get(domain)?.nextAllowedAt || 0) - Date.now();
    if (remaining <= 0) {
      domainRateLimits.delete(domain);
      notifyRateLimitListeners();
      return;
    }
    notifyRateLimitListeners();
    await delay(remaining);
  }
};

const withRetryingRequest = async <T>(work: () => Promise<T>) => {
  activeRetryingRequests += 1;
  notifyRateLimitListeners();
  try {
    return await work();
  } finally {
    activeRetryingRequests = Math.max(0, activeRetryingRequests - 1);
    notifyRateLimitListeners();
  }
};

export const subscribeToServerRateLimitStatus = (listener: (status: ServerRateLimitStatus) => void) => {
  rateLimitListeners.add(listener);
  notifyRateLimitListeners();
  return () => {
    rateLimitListeners.delete(listener);
  };
};

const applyClientFilters = <T>(rows: T[], params: ListParams<T>): T[] => {
  let result = [...rows];

  if (params.filter) result = result.filter(params.filter);

  if (params.sort) {
    result.sort((a: any, b: any) => {
      const valA = a[params.sort!];
      const valB = b[params.sort!];
      if (valA === valB) return 0;
      return valA < valB ? -1 : 1;
    });
  }

  if (params.page !== undefined && params.pageSize !== undefined) {
    const start = (params.page - 1) * params.pageSize;
    result = result.slice(start, start + params.pageSize);
  }

  return result;
};

export class ServerRepository<T extends BaseEntity> {
  private database = db;
  private entityType: string;
  private pkField: string;

  constructor(entityType: string, pkField: string) {
    this.entityType = entityType;
    this.pkField = pkField;
  }

  private async request(path: string, init?: RequestInit, hasRetriedAfterCooldown = false, hasRetriedAfterRenew = false): Promise<any> {
    if (this.database !== db) throw new Error('Workspace changed; request cancelled.');
    const domain = getDomainKey(path);
    await waitForDomainAvailability(domain);

    if (this.database !== db) throw new Error('Workspace changed; request cancelled.');
    const headers = new Headers(init?.headers as HeadersInit | undefined);
    const token = getAuthToken();
    if (token) headers.set('Authorization', `Bearer ${token}`);
    const response = await fetch(buildUrl(path), { ...init, headers });
    if (this.database !== db) throw new Error('Workspace changed; response ignored.');
    if (response.status === 401) {
      if (!hasRetriedAfterRenew) {
        const renewed = await attemptSessionRenewal();
        if (this.database !== db) throw new Error('Workspace changed; renewal ignored.');
        if (renewed) {
          return this.request(path, init, hasRetriedAfterCooldown, true);
        }
      }
      if (getAuthToken() === token) clearAuthSession();
      throw new Error('Unauthorized');
    }
    if (response.status === 404) return undefined;
    if (response.status === 403) {
      throw new Error('Forbidden');
    }
    if (response.status === 429 && !hasRetriedAfterCooldown) {
      const waitMs = parseRetryAfterMs(response.headers.get('Retry-After'));
      setRateLimitCooldown(domain, waitMs);
      return withRetryingRequest(async () => {
        await waitForDomainAvailability(domain);
        return this.request(path, init, true, hasRetriedAfterRenew);
      });
    }
    if (response.status === 429) {
      const waitMs = parseRetryAfterMs(response.headers.get('Retry-After'));
      setRateLimitCooldown(domain, waitMs);
      throw new Error(`ServerRepository ${this.entityType} is busy. Please retry after a short pause.`);
    }
    if (!response.ok) {
      const text = await response.text();
      throw new Error(`ServerRepository ${this.entityType} failed: ${response.status} ${text}`);
    }
    if (response.status === 204) return undefined;
    return response.json();
  }

  private encodeFilters(params?: ListParams<T>) {
    const search = new URLSearchParams();
    if (params?.where && Object.keys(params.where).length > 0) {
      search.set('filters', JSON.stringify(params.where));
    }
    return search.toString() ? `?${search.toString()}` : '';
  }

  private cachedTable() {
    const allowed = ['assets', 'projects', 'workLogs', 'documents', 'links', 'scoutingPhotos', 'scoutingPhotoCategories'];
    return allowed.includes(this.entityType) ? this.database.table<T>(this.entityType) : null;
  }
  async list(params: ListParams<T> = {}): Promise<T[]> {
    if (this.database !== db) throw new Error('Workspace changed.');
    const table = this.cachedTable();
    if (table && await this.database.syncState.get('global_sync')) {
      return applyClientFilters((await table.toArray()).filter(row => !row.deleted_at), params);
    }
    const rows = await this.request('/api/' + this.entityType + this.encodeFilters(params));
    const arrayRows: T[] = Array.isArray(rows) ? rows : [];
    if (table && this.database === db) await table.bulkPut(arrayRows);
    return applyClientFilters(arrayRows, params);
  }
  async get(id: string): Promise<T | undefined> {
    if (this.database !== db) throw new Error('Workspace changed.');
    const table = this.cachedTable();
    if (table && await this.database.syncState.get('global_sync')) {
      const row = await table.get(id); return row?.deleted_at ? undefined : row;
    }
    const row = await this.request('/api/' + this.entityType + '/' + encodeURIComponent(id));
    if (!row || row.deleted_at) return undefined;
    if (table && this.database === db) await table.put(row);
    return row as T;
  }

  async upsert(item: T): Promise<T> {
    const pk = (item as any)[this.pkField];
    const method = pk ? 'PUT' : 'POST';
    const url = pk ? `/api/${this.entityType}/${pk}` : `/api/${this.entityType}`;
    const now = new Date().toISOString();
    const payload: Record<string, any> = { ...item, updated_at: now };
    if (!payload.created_at) payload.created_at = now;

    const saved = await this.request(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const table = this.cachedTable();
    if (table && saved && this.database === db) await table.put(saved);
    return saved as T;
  }

  async remove(id: string): Promise<void> {
    await this.request(`/api/${this.entityType}/${id}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
    });
  }

  async bulkUpsert(items: T[]): Promise<void> {
    for (const item of items) {
      await this.upsert(item);
    }
  }
}
